import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useAuth } from "@/auth/useAuth"
import { useCustomerProfile } from "@/hooks/useCustomerProfile"

import { Button } from "./button"
import { CustomerInfo } from "./customer-info"
import { SavedPaymentMethodsDropdown } from "./saved-payment-methods-dropdown"
import { Separator } from "../ui/separator"
import { Switch } from "../ui/switch"
import { MESSENGERS } from "@/config/contacts"

type BurgerContentProps = {
  onClose: () => void
}

export function BurgerContent({ onClose }: BurgerContentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated, customers, selectedCustomer, switchCustomer, logout, isLoading, isMasked } = useAuth()
  const { profile, isLoading: isProfileLoading, updateMarketing } = useCustomerProfile()

  const handleLogout = async () => {
    await logout()
    onClose()
    navigate("/login", { replace: true })
  }

  const handleSwitchCustomer = async () => {
    await switchCustomer()
    onClose()
    navigate("/select-customer")
  }

  return (
    <div className="flex flex-1 flex-col">
      {isAuthenticated && selectedCustomer && profile ? (
        <>
          <div className="flex flex-col">
            <CustomerInfo
              customerType={profile.customerType}
              name={profile.name}
              email={profile.email}
              phone={profile.phone}
              className="mb-5"
            />
            <label className="mb-4 flex items-center justify-between gap-4">
              <span className="text-[16px] leading-[22px] text-foreground">{t("customerProfile.marketing")}</span>
              <Switch
                checked={profile.marketing}
                disabled={isProfileLoading || updateMarketing.isPending || isLoading}
                onCheckedChange={(checked) => updateMarketing.mutate(checked)}
                className="data-[state=checked]:bg-brand data-[state=unchecked]:bg-muted"
              />
            </label>

            <SavedPaymentMethodsDropdown enabled={isAuthenticated && !!selectedCustomer} />
            
            {customers.length > 1 && !isMasked ? (
              <Button
                type="button"
                variant="link"
                className="h-auto w-fit px-0 py-0 text-base text-brand"
                onClick={() => void handleSwitchCustomer()}
                disabled={isLoading}
              >
                {t("customerSelect.switchCustomer")}
              </Button>
            ) : null}
          </div>
          <Separator className="sm:my-8 my-6" />
        </>
      ) : null}
      <a
        href="tel:+78122070007"
        className="block text-[28px] leading-9 font-semibold text-foreground"
      >
        +7 812 207-00-07
      </a>
      <p className="mt-2 text-base leading-[22px] text-muted-foreground">Ежедневно 9:00–21:00</p>

      <div className="flex gap-4 mt-4">
        {MESSENGERS.map(({ name, href, Icon }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={name}
            className="inline-flex size-[56px] items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-white"
          >
            <Icon className="size-[24px]" />
          </a>
        ))}
      </div>
      {isAuthenticated ? (
        <Button
          variant="secondary"
          className="mt-auto w-full py-[18px] text-base"
          onClick={() => void handleLogout()}
          disabled={isLoading}
        >
          Выйти из аккаунта
        </Button>
      ) : null}
    </div>
  )
}
